import { useMutation } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { type SubmitEvent, useMemo, useState } from 'react'

import type { ChangePasswordRequest } from '@/bindings/ChangePasswordRequest'
import type { Channel } from '@/bindings/Channel'
import type { DeleteAccountRequest } from '@/bindings/DeleteAccountRequest'
import type { CodeRequestedResponse } from '@/bindings/CodeRequestedResponse'
import type { IdentifierRequest } from '@/bindings/IdentifierRequest'
import type { IdentifierVerifyRequest } from '@/bindings/IdentifierVerifyRequest'
import type { Language } from '@/bindings/Language'
import type { PlatformIdentifierRequest } from '@/bindings/PlatformIdentifierRequest'
import type { PlatformIdentifierVerifyRequest } from '@/bindings/PlatformIdentifierVerifyRequest'
import type { RemoveIdentifierRequest } from '@/bindings/RemoveIdentifierRequest'
import type { UpdateProfileRequest } from '@/bindings/UpdateProfileRequest'
import { Button } from '@/components/button'
import { Field, Input, PasswordInput, Select } from '@/components/field'
import { FormError } from '@/components/form-error'
import { Card, Page } from '@/components/layout'
import { codeRejected, useCodeStatus } from '@/features/auth/code-status'
import { CodeStatusPanel } from '@/features/auth/code-status-panel'
import { emptyIdentifierDraft, toLoginIdentifier } from '@/features/auth/identifier-draft'
import { useSmsCoverage } from '@/features/auth/sms-coverage'
import { PasswordChecklist } from '@/features/auth/password-checklist'
import { ApiError, authPath, request } from '@/lib/api'
import { type System, type UserOf, clearAuth, updateAuth, useAuth } from '@/lib/auth-store'
import { LANGUAGES, LANGUAGE_NAMES, isLanguage } from '@/lib/i18n/languages'
import { useLanguage } from '@/lib/i18n/store'
import { useT } from '@/lib/i18n/translate'
import { nationOptions } from '@/lib/nations'
import {
  PASSWORD_MIN_PLATFORM,
  PASSWORD_MIN_RESTAURANT,
  passwordProblem,
  useCommonPasswords,
} from '@/lib/password'

/** Route this ApiError to one field when its code matches, else let it stay form-level. */
function onField(error: unknown, matches: (code: string) => boolean): ApiError | undefined {
  return error instanceof ApiError && matches(error.code) ? error : undefined
}

/**
 * Account self-service (C8) for both systems: names + language, change
 * password, add/change/remove login identifiers (verify-to-activate), delete.
 */
export function ProfilePage<S extends System>({ system }: { system: S }) {
  const t = useT()
  const auth = useAuth(system)

  // Auth loss mid-visit: RequireAuth navigates away; render nothing meanwhile.
  if (auth.status !== 'authenticated') return null

  return (
    <Page width="sm" title={t('profile.title')} className="flex flex-col gap-4">
      <DetailsCard system={system} user={auth.user} />
      <PasswordCard system={system} />
      <IdentifiersCard system={system} user={auth.user} />
      <DeleteCard system={system} />
    </Page>
  )
}

function DetailsCard<S extends System>({ system, user }: { system: S; user: UserOf<S> }) {
  const t = useT()
  const uiLanguage = useLanguage()
  const [firstName, setFirstName] = useState(user.first_name ?? '')
  const [lastName, setLastName] = useState(user.last_name ?? '')
  const [language, setLanguage] = useState<Language>(
    isLanguage(user.preferred_language) ? user.preferred_language : uiLanguage,
  )

  // Changes landing from elsewhere (avatar-menu language, another card) refresh
  // the form; otherwise Save would PUT the stale values back.
  const [seen, setSeen] = useState(user)
  if (
    seen.first_name !== user.first_name ||
    seen.last_name !== user.last_name ||
    seen.preferred_language !== user.preferred_language
  ) {
    setSeen(user)
    setFirstName(user.first_name ?? '')
    setLastName(user.last_name ?? '')
    if (isLanguage(user.preferred_language)) setLanguage(user.preferred_language)
  }

  // PUT semantics: blank clears. The i18n store follows the saved preference
  // through the auth-store subscription (D2) — nothing more to call.
  const save = useMutation({
    mutationFn: () => {
      const body: UpdateProfileRequest = {
        first_name: firstName.trim() || null,
        last_name: lastName.trim() || null,
        preferred_language: language,
      }
      return request<UserOf<S>>(authPath(system, '/profile'), { method: 'PUT', body })
    },
    onSuccess: (user) => updateAuth(system, { user }),
  })

  function submit(event: SubmitEvent) {
    event.preventDefault()
    save.mutate()
  }

  return (
    <Card className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold">{t('profile.detailsTitle')}</h2>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label={t('auth.firstName')}>
          <Input autoComplete="given-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
        </Field>
        <Field label={t('auth.lastName')}>
          <Input autoComplete="family-name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </Field>
        <Field label={t('language.label')}>
          <Select value={language} onChange={(e) => setLanguage(e.target.value as Language)}>
            {LANGUAGES.map((lang) => (
              <option key={lang} value={lang}>
                {LANGUAGE_NAMES[lang]}
              </option>
            ))}
          </Select>
        </Field>
        {save.isSuccess && (
          <p role="status" className="text-muted text-sm">
            {t('profile.saved')}
          </p>
        )}
        <FormError error={save.error} />
        <Button type="submit" loading={save.isPending}>
          {t('common.save')}
        </Button>
      </form>
    </Card>
  )
}

function PasswordCard({ system }: { system: System }) {
  const t = useT()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')

  const min = system === 'platform' ? PASSWORD_MIN_PLATFORM : PASSWORD_MIN_RESTAURANT
  const commonPasswords = useCommonPasswords()
  const [showProblem, setShowProblem] = useState(false)
  const problem = passwordProblem(next, min, commonPasswords)

  const change = useMutation({
    mutationFn: () => {
      const body: ChangePasswordRequest = { current_password: current, new_password: next }
      return request<void>(authPath(system, '/change-password'), { method: 'POST', body })
    },
    onSuccess: () => {
      setCurrent('')
      setNext('')
    },
  })

  function submit(event: SubmitEvent) {
    event.preventDefault()
    if (problem) {
      setShowProblem(true)
      return
    }
    change.mutate()
  }

  // Wrong current password stays inline (D1: no session-loss redirect on it).
  const currentError = onField(change.error, (code) => code === 'INVALID_CREDENTIALS')
  const nextError =
    onField(change.error, (code) => code.startsWith('PASSWORD_')) ??
    (showProblem && problem ? t(problem.key, problem.params) : undefined)

  return (
    <Card className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold">{t('profile.passwordTitle')}</h2>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label={t('auth.currentPassword')} error={currentError}>
          <PasswordInput
            autoComplete="current-password"
            required
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </Field>
        <Field
          label={t('auth.newPassword')}
          hint={<PasswordChecklist password={next} min={min} />}
          error={nextError}
        >
          <PasswordInput
            autoComplete="new-password"
            required
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
        </Field>
        {change.isSuccess && (
          <p role="status" className="text-muted text-sm">
            {t('profile.passwordChanged')}
          </p>
        )}
        <FormError error={currentError ?? nextError ? undefined : change.error} />
        <Button type="submit" loading={change.isPending}>
          {t('profile.passwordTitle')}
        </Button>
      </form>
    </Card>
  )
}

type IdentifierFlow =
  | { kind: 'idle' }
  | { kind: 'edit'; channel: Channel }
  | { kind: 'pending'; channel: Channel }
  | { kind: 'remove'; channel: Channel }

const IDLE: IdentifierFlow = { kind: 'idle' }

function IdentifiersCard<S extends System>({ system, user }: { system: S; user: UserOf<S> }) {
  const t = useT()
  const language = useLanguage()
  const platform = system === 'platform'
  const nations = useMemo(() => nationOptions(language), [language])
  const [flow, setFlow] = useState<IdentifierFlow>(IDLE)
  const [draft, setDraft] = useState(emptyIdentifierDraft)
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [resent, setResent] = useState(false)
  const {
    status,
    record: recordStatus,
    clear: clearStatus,
  } = useCodeStatus('identifier', platform ? `platform:${draft.email.trim()}` : JSON.stringify(toLoginIdentifier(draft)))
  const smsCovered = useSmsCoverage(!platform)

  const phone = 'phone' in user ? user.phone : null
  const channels: readonly Channel[] = platform ? ['email'] : ['email', 'phone']
  // T11.2: never down to zero login methods; the backend enforces, the UI disables.
  const removable = Boolean(phone && user.email)

  // Also the resend; the answer is uniform whether the identifier is taken (C8).
  const requestCode = useMutation({
    mutationFn: () => {
      const body: IdentifierRequest | PlatformIdentifierRequest = platform
        ? { email: draft.email.trim(), password, language }
        : { identifier: toLoginIdentifier(draft), password, language }
      return request<CodeRequestedResponse>(authPath(system, '/identifier/request'), { method: 'POST', body })
    },
    onSuccess: recordStatus,
  })

  const verify = useMutation({
    mutationFn: () => {
      const body: IdentifierVerifyRequest | PlatformIdentifierVerifyRequest = platform
        ? { email: draft.email.trim(), code: code.trim() }
        : { identifier: toLoginIdentifier(draft), code: code.trim() }
      return request<UserOf<S>>(authPath(system, '/identifier/verify'), { method: 'POST', body })
    },
    onSuccess: (user) => {
      clearStatus()
      updateAuth(system, { user })
      close()
    },
  })

  const remove = useMutation({
    mutationFn: (channel: Channel) => {
      const body: RemoveIdentifierRequest = { channel, password }
      return request<UserOf<S>>(authPath(system, '/identifier/remove'), { method: 'POST', body })
    },
    onSuccess: (user) => {
      updateAuth(system, { user })
      close()
    },
  })

  function reset(next: IdentifierFlow) {
    setFlow(next)
    setDraft(emptyIdentifierDraft('channel' in next ? next.channel : undefined))
    setPassword('')
    setCode('')
    setResent(false)
    requestCode.reset()
    verify.reset()
    remove.reset()
  }

  const open = reset
  const close = () => reset(IDLE)

  function submitRequest(event: SubmitEvent) {
    event.preventDefault()
    if (flow.kind !== 'edit') return
    const { channel } = flow
    requestCode.mutate(undefined, { onSuccess: () => setFlow({ kind: 'pending', channel }) })
  }

  function resend() {
    requestCode.mutate(undefined, { onSuccess: () => setResent(true) })
  }

  function submitVerify(event: SubmitEvent) {
    event.preventDefault()
    verify.mutate()
  }

  function submitRemove(event: SubmitEvent) {
    event.preventDefault()
    if (flow.kind === 'remove') remove.mutate(flow.channel)
  }

  const passwordField = (error: unknown) => (
    <Field
      label={t('auth.password')}
      hint={t('profile.reenterHint')}
      error={onField(error, (code) => code === 'INVALID_CREDENTIALS')}
    >
      <PasswordInput
        autoComplete="current-password"
        required
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
    </Field>
  )

  const formLevel = (error: unknown) =>
    onField(error, (code) => code === 'INVALID_CREDENTIALS') ? undefined : error

  const cancelButton = (
    <Button type="button" variant="ghost" onClick={close}>
      {t('common.cancel')}
    </Button>
  )

  return (
    <Card className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold">{t('profile.loginTitle')}</h2>
      {channels.map((channel) => {
        const value = channel === 'phone' ? phone : user.email
        const active = flow.kind !== 'idle' && flow.channel === channel
        return (
          <div key={channel} className="flex flex-col gap-4">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-medium">{t(channel === 'phone' ? 'auth.phone' : 'auth.email')}</p>
                <p className="text-muted truncate text-sm">{value ?? t('profile.notSet')}</p>
              </div>
              <div className="flex shrink-0 gap-2">
                <Button size="sm" variant="secondary" onClick={() => open({ kind: 'edit', channel })}>
                  {t(value ? 'profile.change' : 'profile.add')}
                </Button>
                {!platform && value != null && (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={!removable}
                    onClick={() => open({ kind: 'remove', channel })}
                  >
                    {t('profile.remove')}
                  </Button>
                )}
              </div>
            </div>

            {active && flow.kind === 'edit' && (
              <form onSubmit={submitRequest} className="flex flex-col gap-4">
                {channel === 'phone' ? (
                  <>
                    <Field label={t('auth.nation')}>
                      <Select value={draft.nation} onChange={(e) => setDraft({ ...draft, nation: e.target.value })}>
                        {nations.map((nation) => (
                          <option key={nation.code} value={nation.code}>
                            {nation.label}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <Field
                      label={t('auth.phoneNumber')}
                      hint={smsCovered(draft.nation) ? undefined : t('auth.smsUnsupportedHint')}
                      error={onField(requestCode.error, (code) => ['INVALID_PHONE', 'IDENTIFIER_ALREADY_HELD'].includes(code))}
                    >
                      <Input
                        type="tel"
                        inputMode="tel"
                        autoComplete="tel"
                        required
                        value={draft.number}
                        onChange={(e) => setDraft({ ...draft, number: e.target.value })}
                      />
                    </Field>
                  </>
                ) : (
                  <Field
                    label={t('auth.email')}
                    error={onField(requestCode.error, (code) => ['INVALID_EMAIL', 'IDENTIFIER_ALREADY_HELD'].includes(code))}
                  >
                    <Input
                      type="email"
                      autoComplete="email"
                      required
                      value={draft.email}
                      onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                    />
                  </Field>
                )}
                {passwordField(requestCode.error)}
                <FormError
                  error={onField(requestCode.error, (code) =>
                    ['INVALID_CREDENTIALS', 'INVALID_PHONE', 'INVALID_EMAIL', 'IDENTIFIER_ALREADY_HELD'].includes(code),
                  )
                    ? undefined
                    : requestCode.error}
                />
                <div className="flex gap-2">
                  <Button type="submit" loading={requestCode.isPending}>
                    {t('auth.sendCode')}
                  </Button>
                  {cancelButton}
                </div>
              </form>
            )}

            {active && flow.kind === 'pending' && (
              <form onSubmit={submitVerify} className="flex flex-col gap-4">
                <p className="text-muted text-sm">
                  {t('profile.pendingIdentifier', {
                    identifier: channel === 'phone' ? draft.number : draft.email,
                  })}
                </p>
                <Field label={t('auth.code')}>
                  <Input
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                  />
                </Field>
                {resent && (
                  <p role="status" className="text-muted text-sm">
                    {t('auth.codeRequested')}
                  </p>
                )}
                <FormError error={verify.error} />
                <FormError error={requestCode.error} />
                <CodeStatusPanel
                  status={status}
                  channel={channel}
                  flow="verify"
                  smsCovered={platform || smsCovered(draft.nation)}
                  codeRejected={codeRejected(verify.error)}
                  onResend={resend}
                  resendPending={requestCode.isPending}
                />
                <div className="flex gap-2">
                  <Button type="submit" loading={verify.isPending}>
                    {t('profile.verify')}
                  </Button>
                  {cancelButton}
                </div>
              </form>
            )}

            {active && flow.kind === 'remove' && (
              <form onSubmit={submitRemove} className="flex flex-col gap-4">
                {passwordField(remove.error)}
                <FormError error={formLevel(remove.error)} />
                <div className="flex gap-2">
                  <Button type="submit" variant="danger" loading={remove.isPending}>
                    {t('profile.remove')}
                  </Button>
                  {cancelButton}
                </div>
              </form>
            )}
          </div>
        )
      })}
    </Card>
  )
}

function DeleteCard({ system }: { system: System }) {
  const t = useT()
  const navigate = useNavigate()
  const [password, setPassword] = useState('')

  // 204 also cleared the cookie. A deleted restaurant user lands on the Romi
  // intro (PRD, H1); the platform host has no public page, so its guard
  // routes to login page.
  const del = useMutation({
    mutationFn: () => {
      const body: DeleteAccountRequest = { password }
      return request<void>(authPath(system, '/delete-account'), { method: 'POST', body })
    },
    onSuccess: async () => {
      // Leave the guarded tree first, or its own redirect to login wins.
      if (system === 'restaurant') await navigate({ to: '/', replace: true })
      clearAuth(system)
    },
  })

  function submit(event: SubmitEvent) {
    event.preventDefault()
    del.mutate()
  }

  const passwordError = onField(del.error, (code) => code === 'INVALID_CREDENTIALS')

  return (
    <Card className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold">{t('profile.deleteTitle')}</h2>
      <p className="text-muted text-sm">{t('profile.deleteIntro')}</p>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label={t('auth.password')} hint={t('profile.reenterHint')} error={passwordError}>
          <PasswordInput
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <FormError error={passwordError ? undefined : del.error} />
        <Button type="submit" variant="danger" loading={del.isPending}>
          {t('profile.deleteConfirm')}
        </Button>
      </form>
    </Card>
  )
}
